import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/MarketingLayout";
import Seo, { BlogPostingJsonLd } from "@/components/Seo";
import { api } from "@/lib/api";
import Markdown from "@/components/Markdown";
import * as Lucide from "lucide-react";

export function BlogIndex() {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState(["All"]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/blog/categories").then((r) => setCats(["All", ...r.data]));
  }, []);
  useEffect(() => {
    const params = {};
    if (cat !== "All") params.category = cat;
    if (q) params.q = q;
    api.get("/blog/posts", { params }).then((r) => setPosts(r.data)).catch(() => {});
  }, [cat, q]);

  return (
    <MarketingLayout>
      <Seo title="AI Blog & Insights" path="/blog" description="Tutorials, guides, and news on AI tools, marketing, SEO, image generation, automation, and more." />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-6">
        <div className="bx-chip"><Lucide.BookOpen size={12} /> AI INSIGHTS</div>
        <h1 className="bx-heading mt-4 text-4xl sm:text-5xl font-extrabold">AI Blog & Insights</h1>
        <p className="text-slate-400 mt-3 max-w-2xl">Tutorials, guides, and news on AI tools, marketing, SEO, image generation, automation, and more.</p>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[220px_1fr] gap-8 pb-20">
        <aside>
          <div className="bx-card p-4 sticky top-20">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Categories</div>
            <div className="flex flex-col gap-1">
              {cats.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`text-left px-3 py-2 rounded-lg text-sm transition ${cat === c ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white" : "text-slate-300 hover:bg-white/5"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </aside>
        <div>
          <div className="relative max-w-md mb-6">
            <Lucide.Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input data-testid="blog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search blog posts…" className="bx-input pl-9" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.35, delay: (i % 9) * 0.04 }}
              >
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="bx-card overflow-hidden block">
                  <div className="h-40 bg-gradient-to-br from-purple-600/20 to-blue-600/15 flex items-center justify-center overflow-hidden">
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }}>
                      <Lucide.Sparkles size={36} className="text-purple-300/60" />
                    </motion.div>
                  </div>
                  <div className="p-5">
                    <div className="text-xs text-purple-300">{p.category}</div>
                    <div className="bx-heading mt-1 text-lg font-semibold line-clamp-2">{p.title}</div>
                    <p className="text-sm text-slate-400 mt-2 line-clamp-2">{p.excerpt}</p>
                    <div className="text-xs text-slate-500 mt-3">{new Date(p.created_at).toLocaleDateString()} · {p.author}</div>
                  </div>
                </Link>
              </motion.div>
            ))}
            {!posts.length && <div className="col-span-full text-slate-400 text-center py-20">No posts found.</div>}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  useEffect(() => { api.get(`/blog/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false)); }, [slug]);

  if (post === null) return <MarketingLayout><div className="text-center py-20 text-slate-400">Loading…</div></MarketingLayout>;
  if (post === false) return <MarketingLayout><div className="text-center py-20 text-slate-400">Post not found. <Link to="/blog" className="text-purple-300 underline">Back to blog</Link></div></MarketingLayout>;

  return (
    <MarketingLayout>
      <Seo title={post.title} path={`/blog/${slug}`} description={post.meta_description || post.excerpt} type="article" />
      <BlogPostingJsonLd
        title={post.title}
        description={post.meta_description || post.excerpt}
        image={post.cover_image || `https://binarex.co/og-image.png`}
        datePublished={post.created_at}
        author={post.author}
        url={`https://binarex.co/blog/${slug}`}
      />
      <article className="max-w-3xl mx-auto px-5 lg:px-8 py-14">
        <Link to="/blog" className="text-sm text-slate-400 inline-flex items-center gap-1 hover:text-white"><Lucide.ArrowLeft size={14} /> Back to blog</Link>
        <div className="mt-4 text-xs text-purple-300">{post.category}</div>
        <h1 className="bx-heading mt-2 text-3xl sm:text-4xl font-extrabold leading-tight">{post.title}</h1>
        <div className="text-xs text-slate-500 mt-3">{new Date(post.created_at).toLocaleDateString()} · {post.author}</div>
        <div className="mt-6"><Markdown>{post.content}</Markdown></div>
        <div className="mt-8 flex flex-wrap gap-2">
          {(post.tags || []).map((t) => (<span key={t} className="bx-chip">{t}</span>))}
        </div>
      </article>
    </MarketingLayout>
  );
}
